import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Tabs } from '@factorialco/deprecated-design-system'

import type CardTabs from 'modules/core/components/Card/CardTabs'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { isManager } from 'modules/employees/lib/utils'
import type Review from 'modules/reviews/store/models/v2/Review'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsTeamLead } from 'modules/teams/store/userIsTeamLead'

type CardTabsProps = React.ComponentProps<typeof CardTabs>

interface Props {
  form: Review
  canEditReview: boolean
}

export default function ReviewShowPageTabs({ form, canEditReview }: Props) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { employee } = useCurrentSession()
  const formId = form.id

  const canSeeInfo =
    form?.attributes.visible_to_managers &&
    (isManager(employee) ||
      (users.current.employee && userIsTeamLead(teams, memberships, users.current.employee.id)))

  let tabs: CardTabsProps['tabs'] = []

  if (canEditReview) {
    tabs = tabs.concat([
      {
        href: navigation.performance.reviews.id.configuration.fullPath({ id: formId }),
        onClick: () => track(trackingEvents.reviews.reviewConfiguration),
        name: i18n.t('surveys.form.tabs.index'),
      },
      {
        href: navigation.performance.reviews.id.questions.fullPath({ id: formId }),
        onClick: () => track(trackingEvents.reviews.reviewQuestions),
        name: i18n.t('surveys.form.tabs.questions'),
      },
    ])
  }

  if (canEditReview || canSeeInfo) {
    tabs = tabs.concat([
      {
        href: navigation.performance.reviews.id.participants.fullPath({ id: formId }),
        onClick: () => track(trackingEvents.reviews.reviewParticipants),
        name: i18n.t('surveys.form.tabs.participants'),
      },
    ])
  }

  if (form && !form.isDraft) {
    tabs = tabs.concat([
      {
        href: navigation.performance.reviews.id.reviews.fullPath({ id: formId }),
        name: i18n.t('surveys.form.tabs.reviews'),
      },
      {
        href: navigation.performance.reviews.id.writeReviews.fullPath({ id: formId }),
        name: i18n.t('surveys.form.tabs.write_reviews'),
      },
    ])
  }

  if (canEditReview || canSeeInfo) {
    tabs = tabs.concat([
      {
        href: navigation.performance.reviews.id.analytics.fullPath({ id: formId }),
        onClick: () => track(trackingEvents.reviews.reviewAnalytics),
        name: i18n.t('surveys.form.tabs.analytics'),
      },
    ])
  }

  if (!tabs.length) return null
  const currentId = tabs.find((tab) => pathname.includes(tab.href))?.href || pathname

  return (
    <Tabs currentId={currentId} onTabClicked={navigate}>
      {tabs.map((tab) => (
        <Tabs.Tab key={tab.href} id={tab.href} label={tab.name} />
      ))}
    </Tabs>
  )
}
